/* eslint-disable no-console */

import { Buffer } from 'buffer';

import ReconnectingWebSocket from 'reconnectingwebsocket';

import type {
  HeaderType,
  AppSyncSubscriptionIClient,
  SubscriptionInputVariablesType,
  SubscriptionMessageType,
  SubscriptionType,
} from './appSyncSubscriptionIClient';

const makeStartSubscriptionMessageData = (
  id: string,
  query: string,
  variables: SubscriptionInputVariablesType,
  header: HeaderType,
) => {
  // サブスクリプションの登録
  const payload = {
    data: JSON.stringify({
      query,
      variables,
    }),
    extensions: {
      authorization: header,
    },
  };
  return {
    type: 'start',
    id,
    payload,
  };
};

/**
 * AppSyncSubscriptionClient
 */
export class AppSyncSubscriptionClient implements AppSyncSubscriptionIClient {
  /** エンドポイント */
  endPoint: string;

  /** ホスト */
  host: string;

  /** コネクションタイムアウトms */
  connectionTimeoutMs: number;

  /** 接続されたか */
  isConnected: boolean;

  /** エラーリスト */
  errors: never[];

  /** エラー時の処理 */
  onError: (error: any[]) => void;

  /** 接続 */
  connection: ReconnectingWebSocket | null;

  /**
   * Constructor
   * @param {*} url URL
   */
  constructor(url: string) {
    this.endPoint = url.replace('https://', 'wss://').replace('appsync-api', 'appsync-realtime-api');
    this.host = url.replace('https://', '').replace('/graphql', '');
    this.connectionTimeoutMs = 0; // KeepAliveの周期[ms]
    this.isConnected = false;
    this.errors = [];
    this.connection = null;
    this.onError = (error) => console.log(error);
  }

  /**
   * base64encodeJson
   * @param json JSON
   * @returns JSON
   */
  base64encodeJson(json: any) {
    return Buffer.from(JSON.stringify(json)).toString('base64');
  }

  /**
   * getLogMessage
   * @param msg メッセージ
   * @returns メッセージ
   */
  protected getLogMessage(msg: string) {
    return `[WebSocket]${(new Date()).toLocaleString()}:${msg}`;
  }

  /**
   * 接続
   * @param jwtToken jwtToken
   * @param subscriptions subscriptions
   * @param onError onError
   */
  connect(
    jwtToken: string,
    subscriptions: SubscriptionType[],
    onError: (error: any[]) => void,
  ) {
    this.onError = onError;
    const header = {
      host: this.host,
      Authorization: jwtToken,
    };
    const requestUrl = `${this.endPoint}?header=${this.base64encodeJson(header)}&payload=${this.base64encodeJson({})}`;

    console.debug(this.getLogMessage('Start'));
    this.disconnect();
    const connection = new ReconnectingWebSocket(
      requestUrl,
      ['graphql-ws'],
      {
        debug: false,
        reconnectInterval: 5000,
        maxReconnectAttempts: null,
      },
    );
    this.connection = connection;

    // コネクションが接続/再接続された時の動き
    this.connection.onopen = () => {
      console.debug(this.getLogMessage('Open'));
      const initData = JSON.stringify({ type: 'connection_init' });
      connection.send(initData);
      this.isConnected = true;
    };

    // エラーが発生したされた時の動き
    this.connection.onerror = (error) => {
      console.debug(this.getLogMessage('Error occured'), error);
    };

    // メッセージを受け取ったされた時の動き
    this.connection.onmessage = (e) => {
      const message = JSON.parse(e.data as string);
      console.debug(this.getLogMessage(`Message: ${message.type as string}`));
      switch (message.type) {
        case 'start_ack':
          break;
        case 'connection_ack': {
          // 接続確認メッセージ
          this.connectionTimeoutMs = message.payload.connectionTimeoutMs;
          // Subscription開始用のデータを作成する
          for (let i = 0; i < subscriptions.length; i += 1) {
            this.addSubscription(
              header,
              subscriptions[i],
              connection,
            );
          }
          break;
        }
        case 'data': {
          for (let i = 0; i < subscriptions.length; i += 1) {
            if (subscriptions[i].id === message.id) {
              subscriptions[i].onSubscribe(message as SubscriptionMessageType);
              break;
            }
          }
          break;
        }
        case 'ka':
          // Keep Alive メッセージ
          break;
        case 'error':
        case 'connection_error': {
          this.errors = message.payload.errors;
          connection.close();
          break;
        }
        default:

          console.error(this.getLogMessage(`Unknown message type (${message.type})`));
          this.errors = message.payload.errors;
          connection.close();
          break;
      }
    };

    // 通信が切断された時の動き
    this.connection.onclose = () => {
      console.debug(this.getLogMessage('Close'));
      if (this.errors.length > 0) {
        this.onError(this.errors);
      }
      this.isConnected = false;
    };
  }

  /**
   * 接続解除
   */
  disconnect() {
    console.debug(this.getLogMessage('Disconnect'));
    if (this.connection && this.isConnected) {
      this.connection.close();
      console.debug('[WebSocket] Disconnected');
    }
  }

  /**
   * addSubscription
   * @param header header
   * @param subscription subscription
   * @param connection connection
   * @returns ID
   */
  addSubscription(
    header: HeaderType,
    subscription: SubscriptionType,
    connection: ReconnectingWebSocket,
  ) {
    console.debug(this.getLogMessage(`Add sub: ${subscription.id}`));
    const { query, variables } = subscription;
    const data = makeStartSubscriptionMessageData(
      subscription.id,
      query,
      variables,
      header,
    );
    connection.send(JSON.stringify(data));
    return data.id;
  }

  /**
   * removeSubscription
   * @param id ID
   */
  removeSubscription(id: string) {
    console.debug(this.getLogMessage(`Remove sub: ${id}`));
    if (this.connection) {
      const data = JSON.stringify({
        type: 'stop',
        id,
      });
      this.connection.send(data);
      console.debug('[WebSocket] Removed');
    }
  }

  /**
   * 通知イベント
   * @param _id ID
   * @param _data データ
   */
  notifyEvent(_id: string, _data: any): void {
    throw new Error('このメソッドはFakeのみ使用します');
  }
}
